import * as THREE from 'three';
import { box, cyl, M, mat, mesh, texMat, worldBox } from '../materials';
import { latticeTexture, roofTexture, sidingTexture, woodTexture } from '../textures';
import { porchBulbMaterial, TRAILER_BASE, windowMaterial, type WindowKind } from './trailer';

export interface DoubleWideSpec {
  color: string;
  stripe: string;
  /** +1 builds Lot 7's layout; -1 mirrors it end for end (homes across the street). */
  flip: 1 | -1;
}

export const DOUBLE_WIDE = { length: 16, depth: 7.2, height: 2.6, rise: 1.2 } as const;

/**
 * Your double wide from Big Dave's, set on your own lot: two sections, a pitched roof,
 * a porch deck with an awning, and shutters on the windows like a real house.
 * Local frame matches the single-wide: long axis x, front door on the +z wall.
 */
export function buildHomeDoubleWide(spec: DoubleWideSpec): THREE.Group {
  const { length: L, depth: D, height: H, rise } = DOUBLE_WIDE;
  const base = TRAILER_BASE;
  const f = spec.flip;
  const doorX = 4 * f;
  const g = new THREE.Group();
  const trim = mat('#f4f1e6');
  const siding = texMat(sidingTexture(), { color: spec.color });

  g.add(mesh(worldBox(L, H, D, 1), siding, 0, base + H / 2, 0));
  // Stripe and trim bands sit proud of the walls and stop short of the top and bottom faces.
  g.add(box(L + 0.03, 0.18, D + 0.03, mat(spec.stripe), 0, base + H * 0.62, 0, { cast: false }));
  g.add(box(L + 0.04, 0.1, D + 0.04, trim, 0, base + 0.06, 0, { cast: false }));
  g.add(box(L + 0.04, 0.1, D + 0.04, trim, 0, base + H - 0.07, 0, { cast: false }));
  g.add(
    mesh(worldBox(L - 0.1, base, D - 0.1, 0.6), texMat(latticeTexture()), 0, base / 2, 0, { cast: false }),
  );

  // Pitched roof, ridge running the long way.
  const shape = new THREE.Shape();
  shape.moveTo(-D / 2 - 0.35, 0);
  shape.lineTo(D / 2 + 0.35, 0);
  shape.lineTo(0, rise);
  shape.closePath();
  const roofGeo = new THREE.ExtrudeGeometry(shape, { depth: L + 0.5, bevelEnabled: false });
  roofGeo.translate(0, 0, -(L + 0.5) / 2);
  const roof = mesh(roofGeo, texMat(roofTexture(), { color: '#9a8f86', rough: 0.85 }), 0, base + H + 0.01, 0);
  roof.rotation.y = Math.PI / 2;
  g.add(roof);

  // Windows with shutters. Front: living room and kitchen; back and ends: bedrooms.
  const shutter = mat('#2f4a35', { rough: 0.8 });
  const windowAt = (x: number, side: 1 | -1, kind: WindowKind, w = 1.2): void => {
    const z = side * (D / 2 + 0.015);
    g.add(box(w + 0.14, 1.0, 0.05, trim, x, base + 1.55, z, { cast: false }));
    const pane = mesh(
      new THREE.PlaneGeometry(w, 0.86),
      windowMaterial(kind),
      x,
      base + 1.55,
      side * (D / 2 + 0.045),
      {
        cast: false,
      },
    );
    if (side < 0) pane.rotation.y = Math.PI;
    g.add(pane);
    for (const s of [-1, 1])
      g.add(
        box(0.36, 1.05, 0.04, shutter, x + s * (w / 2 + 0.27), base + 1.55, side * (D / 2 + 0.03), {
          cast: false,
        }),
      );
  };
  for (const x of [-6.2, -3.4, -0.4]) windowAt(x * f, 1, x === -3.4 ? 'blinds' : 'glass');
  windowAt(6.6 * f, 1, 'glass', 1.0);
  for (const x of [-5, -0.8, 4.2]) windowAt(x * f, -1, x === 4.2 ? 'foil' : 'dim');
  for (const s of [-1, 1]) {
    const end = mesh(
      new THREE.PlaneGeometry(1.1, 0.86),
      windowMaterial('dim'),
      s * (L / 2 + 0.045),
      base + 1.55,
      1.4,
      {
        cast: false,
      },
    );
    end.rotation.y = (s * Math.PI) / 2;
    g.add(end);
    g.add(box(0.05, 1.0, 1.24, trim, s * (L / 2 + 0.015), base + 1.55, 1.4, { cast: false }));
  }

  // Front door: frame, door, a little window and a knob.
  g.add(box(1.0, 2.1, 0.07, trim, doorX, base + 1.05, D / 2 + 0.02));
  g.add(box(0.88, 2.0, 0.07, mat('#7a2b22'), doorX, base + 1.02, D / 2 + 0.045));
  g.add(
    mesh(new THREE.PlaneGeometry(0.4, 0.4), windowMaterial('glass'), doorX, base + 1.6, D / 2 + 0.085, {
      cast: false,
    }),
  );
  g.add(box(0.06, 0.06, 0.06, M.chrome, doorX - 0.32 * f, base + 0.98, D / 2 + 0.1, { cast: false }));

  // Porch deck with railings, steps and an awning.
  const wood = texMat(woodTexture());
  const deckZ = D / 2 + 0.95;
  g.add(mesh(worldBox(3.4, 0.12, 1.9, 1), wood, doorX, base - 0.06, deckZ));
  for (const dx of [-1.6, 1.6])
    for (const dz of [0.15, 1.75])
      g.add(box(0.12, base - 0.12, 0.12, M.trunk, doorX + dx, (base - 0.12) / 2, D / 2 + dz));
  for (const dx of [-1.6, 1.6]) {
    g.add(box(0.08, 0.95, 0.08, M.trunk, doorX + dx, base + 0.475, D / 2 + 1.82));
    g.add(box(0.07, 0.07, 1.85, M.trunk, doorX + dx, base + 0.9, deckZ));
  }
  for (const s of [-1, 1]) g.add(box(1.1, 0.07, 0.07, M.trunk, doorX + s * 1.1, base + 0.9, D / 2 + 1.82));
  g.add(box(1.0, 0.25, 0.45, wood, doorX, 0.13, D / 2 + 2.1));
  const awning = box(3.7, 0.06, 2.1, mat('#e8e4d6', { side: THREE.DoubleSide }), doorX, base + 2.48, deckZ, {
    cast: true,
  });
  awning.rotation.x = 0.09;
  g.add(awning);
  g.add(box(3.72, 0.16, 0.04, mat('#3f7a4a'), doorX, base + 2.36, D / 2 + 2.0, { cast: false }));
  for (const dx of [-1.72, 1.72])
    g.add(cyl(0.04, 0.04, base + 2.4, M.chrome, doorX + dx, (base + 2.4) / 2, D / 2 + 1.96, 6));

  // Porch light beside the door.
  g.add(box(0.14, 0.2, 0.1, M.black, doorX - 0.7 * f, base + 2.0, D / 2 + 0.06, { cast: false }));
  g.add(
    mesh(
      new THREE.SphereGeometry(0.07, 8, 6),
      porchBulbMaterial(),
      doorX - 0.7 * f,
      base + 1.92,
      D / 2 + 0.12,
      {
        cast: false,
      },
    ),
  );

  // Window AC on the kitchen end and a vent pipe on the roof.
  g.add(box(0.58, 0.44, 0.66, mat('#d8d3c2'), (L / 2 + 0.28) * f, base + 1.3, -1.6));
  g.add(cyl(0.07, 0.07, 0.6, M.steel, -2 * f, base + H + rise * 0.55 + 0.2, -1.4, 8));
  return g;
}
